(ns hai.tasks.ui
  "The completion stamp a done task wears, and the dashed box that stamps
   itself when you complete one. Composing and editing tasks is
   `hai.tasks.form`'s, on every surface."
  (:require
   [hai.ui.tokens :as t]
   [lambdaisland.ornament :as o]))

(defn- hash01
  "Deterministic value in [0, 1) from any seed."
  [seed]
  (/ (mod (hash seed) 100000) 100000))

(defn stamp-for
  "Stamp visuals (rotation, offset, per-side border thickness) derived from a
   task title — same title always renders the same impression. Border range
   1.5-3.8 px so adjacent sides read as visibly uneven hand-stamp pressure
   without the heavy end looking blocky."
  [title]
  (let [r  (fn [salt] (hash01 [title salt]))
        bw (fn [salt] (+ 1.5 (* (r salt) 2.3)))]
    {:rot (- (* (r :rot) 24) 12)
     :dx  (- (* (r :dx) 14) 7)
     :dy  (- (* (r :dy) 12) 6)
     :bt  (bw :bt)
     :br  (bw :br)
     :bb  (bw :bb)
     :bl  (bw :bl)}))

;; ---------------------------------------------------------------------------
;; Styled components
;; ---------------------------------------------------------------------------

(o/defstyled completion-stamp :div
  {:border-style "solid"
   :border-color t/--property-15
   :color t/--property-15
   :border-radius "7px"
   :display "flex" :align-items "center" :justify-content "center"
   :pointer-events "none"
   :user-select "none"
   :-webkit-user-select "none"
   :opacity "0.88"
   :mix-blend-mode "multiply"
   :background-image "radial-gradient(circle at 28% 32%, rgba(255,255,255,0.55) 0%, transparent 32%), radial-gradient(circle at 72% 75%, rgba(255,255,255,0.35) 0%, transparent 28%)"}
  [:.check {:font-weight "700" :line-height "1"}]
  ([{:keys [task-title centered?]}]
   (let [{:keys [rot dx dy bt br bb bl]} (stamp-for task-title)
         ;; The dx/dy jitter is the off-center "stamp pressure" inside a
         ;; stampbox; stamps laid out in a row keep only the rotation.
         transform (if centered?
                     (str "translate(calc(-50% + " dx "px), calc(-50% + " dy "px))"
                          " rotate(" rot "deg)")
                     (str "rotate(" rot "deg)"))]
     [:<>
      {:style {:transform transform
               :border-width (str bt "px " br "px " bb "px " bl "px")}}
      [:span.check "✔"]])))

;; The box a task waits in until it is stamped with [[completion-stamp]] —
;; click (or Enter/Space) toggles completion. One size wherever a task wears
;; it — an open task's chip row, the tasks page's cards and its completed list;
;; a host wanting another sizes it from its own rules. Solid, not dashed: a
;; dashed edge reads as optional, and finishing the task is the whole point.
(o/defstyled stampbox :button
  {:width "26px" :height "26px"
   :border-width "2px" :border-style "solid" :border-color t/--property-3
   :border-radius "8px"
   :background "rgba(255,255,255,0.5)"
   :position "relative"
   :flex "0 0 auto"
   :cursor "pointer" :outline "none"}
  [:&:focus-visible
   {:border-color t/--card-forest
    :box-shadow "0 0 0 2px #F7F6EF, 0 0 0 3.5px #2D3E2A"}]
  [:&.stamped
   {:border-color "rgba(45,62,42,0.18)"
    :background "rgba(255,255,255,0.3)"}]
  ;; Hover feedback only where hover exists — on touch the browser pins :hover
  ;; to the last-tapped element, which would leave it stuck on.
  [:at-media {:hover "hover"}
   ;; Empty: the box darkens, inviting a stamp.
   ["&:not(.stamped):hover" {:border-color t/--card-forest}]
   ;; Stamped: the box holds still. A click lifts the stamp, so the stamp is
   ;; what answers the pointer.
   [:&.stamped:hover
    [completion-stamp {:opacity "1"
                       :border-color t/--card-forest
                       :color t/--card-forest}]]]
  [:&:disabled
   {:cursor "not-allowed"
    :opacity "0.5"}]
  [completion-stamp
   {:position "absolute"
    :left "50%" :top "50%"
    :width "24px" :height "24px"}
   [:.check {:font-size "16px"}]]
  ([{:keys [done? task-title stamp-key disabled? title on-toggle]}]
   [:<>
    {:class      (when done? "stamped")
     :type       "button"
     :disabled   (boolean disabled?)
     :title      (or title
                     (when disabled? "You don't have permission to complete this task")
                     (if done? "Mark incomplete" "Mark complete"))
     :aria-label (if done? "remove stamp" "stamp complete")
     :on-click   on-toggle}
    (when done?
      ^{:key (or stamp-key task-title)}
      [completion-stamp {:task-title task-title :centered? true}])]))

